import React, { useState } from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useExcluirConta } from 'hooks/useContas';
import { ContaType } from 'services/contaService';

interface ModalExcluirContaProps {
    show: boolean;
    onHide: () => void;
    conta: ContaType | null;
}

const ModalExcluirConta: React.FC<ModalExcluirContaProps> = ({ show, onHide, conta }) => {
    const [erro, setErro] = useState('');

    const excluirContaMutation = useExcluirConta();

    const handleConfirm = async () => {
        setErro('');

        if (!conta) {
            setErro('Conta não encontrada');
            return;
        }

        try {
            await excluirContaMutation.mutateAsync(conta.id);
            onHide();
        } catch (error: any) {
            setErro(error?.response?.data?.message || 'Erro ao excluir conta');
        }
    };

    const handleClose = () => {
        setErro('');
        onHide();
    };

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faTrash} className="me-2 text-danger-emphasis" />
                    Excluir Conta
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {erro && (
                    <Alert variant="danger" className="mb-3 border border-danger border-opacity-25 bg-danger-subtle">
                        <div className="text-danger-emphasis">{erro}</div>
                    </Alert>
                )}

                <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle">
                    <div className="text-warning-emphasis">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Atenção!</strong> Esta ação não pode ser desfeita.
                    </div>
                </Alert>

                {conta && (
                    <div>
                        <p>Tem certeza que deseja excluir a conta:</p>
                        <div className="bg-body-secondary bg-opacity-50 p-3 rounded mb-3">
                            <h6 className="mb-1 text-body-emphasis">{conta.nome}</h6>
                            <small className="text-body-secondary d-block">
                                Saldo atual: <span className={conta.saldo >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}>
                                    {formatarMoeda(conta.saldo)}
                                </span>
                            </small>
                            <small className="text-body-secondary">
                                Criada em: {new Date(conta.createdAt).toLocaleDateString('pt-BR')}
                            </small>
                        </div>

                        {conta.saldo !== 0 && (
                            <Alert variant="info" className="small border border-info border-opacity-25 bg-info-subtle">
                                <div className="text-info-emphasis">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                    Esta conta possui saldo {conta.saldo > 0 ? 'positivo' : 'negativo'}.
                                    Considere transferir o valor antes de excluir.
                                </div>
                            </Alert>
                        )}

                        <p className="text-body-secondary small">
                            <strong>Importante:</strong> A conta só poderá ser excluída se não possuir movimentos associados.
                        </p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={excluirContaMutation.isPending}
                >
                    Cancelar
                </Button>
                <Button
                    variant="danger"
                    onClick={handleConfirm}
                    disabled={excluirContaMutation.isPending}
                >
                    {excluirContaMutation.isPending ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Excluindo...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                            Confirmar Exclusão
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalExcluirConta; 